<template>
  <el-dialog
    align-center
    :modelValue="visible"
    :modal-class="`dialog-class ${customClass}`"
    :showClose="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :z-index="2000"
    :append-to-body="appendToBody"
    @closed="onClosed"
  >
    <template v-if="showHeader">
      <!-- ; -->
      <div class="dialog-header">
        <h1>{{ title }}</h1>
        <span class="close-span" @click="close">
          <svg-icon name="icon_close" />
        </span>
      </div>
      <!-- dialog title bar -->
      <slot name="header" />
    </template>
    <!-- dialog content -->
    <div class="dialog-content">
      <el-scrollbar v-if="scroll">
        <slot name="content" />
      </el-scrollbar>
      <div v-else>
        <slot name="content" />
      </div>
    </div>
    <!-- dialog bottom button -->
    <div v-if="showFooter" class="dialog-bottom">
      <template v-if="hasButtonSlot">
        <slot name="button" />
      </template>
      <div v-else class="default-button">
        <el-button class="gray-btn" @click="cancel">{{
          t("common.cancel")
        }}</el-button>
        <el-button class="purple-btn" :loading="loading" @click="confirm">{{
          t("common.confirm")
        }}</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts" setup>
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  appendToBody: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: "",
  },
  scroll: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
});
const { t } = useI18n();
const emits = defineEmits(["confirm", "cancel", "close"]);
const hasButtonSlot = computed(() => !!useSlots().button);
const onClosed = () => {
  emits("close");
};
const close = () => {
  emits("close");
};
const cancel = () => {
  emits("cancel");
};
const confirm = () => {
  emits("confirm");
};
</script>

<style lang="scss" scoped>
@use "index.scss";
</style>
<style lang="scss">
.dialog-class {
  .el-dialog {
    width: auto;
    padding: 0;
    border-radius: var(--border-radius-xl);
  }

  .el-dialog__body {
    display: flex;
    flex-direction: column;
  }

  .el-dialog__header {
    display: none;
  }

  .dialog-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;
    border-bottom: 1px solid rgba(229, 230, 235, 1);

    h1 {
      line-height: 24px;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.5px;
      color: var(--color-font-title);
    }

    .close-span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      position: absolute;
      right: 16px;
      cursor: pointer;
      transition: 0.2s;
      border-radius: var(--border-radius-s);

      svg {
        width: 20px;
        height: 20px;
      }

      &:hover {
        background-color: var(--color-fill-2);
      }

      &:active {
        background-color: var(--color-fill-3);
      }
    }
  }

  .dialog-content {
    flex: 1;
    // 兼容Safari
    // height: 500px;
    // max-height: 500px;
  }

  .dialog-bottom {
    padding: 24px;

    .default-button {
      display: flex;
      justify-content: end;
    }

    button {
      padding: 10px 24px;
      font-size: 16px;
      line-height: 24px;
      height: auto;
      border: none;
      transition: 0.2s;
      border-radius: var(--border-radius-m);
    }

    .purple-btn {
      color: var(--color-brand);
      border: 1px solid transparent;
      background-color: rgba(234, 232, 255, 1);

      &:hover {
        border-color: rgba(200, 191, 255, 1);
      }

      &:active {
        background-color: --color-brand-active;
      }
    }

    .gray-btn {
      color: var(--np-text-color-2);
      border: 1px solid transparent;
      background-color: var(--np-fill-color-1);

      &:hover {
        border-color: var(--np-border-color-1);
        background-color: var(--web-background-color);
      }

      &:active {
        background-color: --color-brand-active;
      }
    }

    .red-btn {
      color: rgba(245, 63, 63, 1);
      border: 1px solid transparent;
      background-color: rgba(255, 236, 232, 1);

      &:hover {
        border-color: rgba(245, 63, 63, 1);
      }

      &:active {
        border-color: rgba(245, 63, 63, 1);
      }
    }
  }
}
</style>
